import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  FormField,
  ListBrowseToolbar,
  PageContainer,
  PageHeader,
  PageHeaderActionSecondary,
  PageHeaderActions,
  PageHeaderLeading,
  PageHeaderStack,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  formatRelativeTimeIso,
  useDebouncedValue,
} from '@yeshuman/sdk'
import { History, MoreHorizontal, UserRound } from 'lucide-react'
import { toast } from 'sonner'

import {
  PlatformAreaError,
  usePlatformAction,
  usePlatformProfiles,
  type PlatformProfile,
} from '../../hooks/use-platform-area'
import {
  IncludeSimulatedSwitch,
  LastActionCell,
  ListPager,
  PLATFORM_PAGE_SIZE,
  PlatformOnlyNotice,
  SimulatedBadge,
  actionLogPath,
  formatScore,
} from './platform-area-parts'

function CompletenessCell({ profile }: { profile: PlatformProfile }) {
  return (
    <div className="space-y-1">
      <span className="text-sm tabular-nums">{formatScore(profile.completeness_score)}</span>
      {profile.completeness_missing.length > 0 ? (
        <p className="max-w-[14rem] text-xs text-muted-foreground">
          Missing {profile.completeness_missing.join(', ').toLowerCase()}
        </p>
      ) : null}
      {profile.marked_for_improvement_at ? (
        <Badge variant="promotionSoft" className="whitespace-nowrap">Marked {formatRelativeTimeIso(profile.marked_for_improvement_at)}</Badge>
      ) : null}
    </div>
  )
}

function CvCell({ cv }: { cv: PlatformProfile['cv'] }) {
  if (!cv) return <Badge variant="secondarySoft" className="whitespace-nowrap">No CV</Badge>
  return (
    <div className="space-y-1">
      <Badge variant={cv.status === 'completed' ? 'successSoft' : 'warningSoft'}>
        {cv.status === 'completed' ? 'Parsed' : cv.status.charAt(0).toUpperCase() + cv.status.slice(1)}
      </Badge>
      <div className="max-w-[10rem] truncate text-xs text-muted-foreground" title={cv.filename}>
        {cv.filename}
      </div>
    </div>
  )
}

function ProfileActions({
  profile,
  onMark,
  onReparse,
}: {
  profile: PlatformProfile
  onMark: () => void
  onReparse: () => void
}) {
  const navigate = useNavigate()
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="ghostMuted" size="iconSm" aria-label={`Actions for ${profile.name}`}>
          <MoreHorizontal className="h-4 w-4" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={onMark}>Mark for improvement</DropdownMenuItem>
        <DropdownMenuItem onSelect={onReparse}>Re-parse content</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate(actionLogPath('profile', profile.id))}>
          Action history
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function PlatformProfilesPage() {
  const [searchDraft, setSearchDraft] = useState('')
  const [includeSimulated, setIncludeSimulated] = useState(false)
  const [offset, setOffset] = useState(0)
  const [markProfile, setMarkProfile] = useState<PlatformProfile | null>(null)
  const [note, setNote] = useState('')
  const [reparseProfile, setReparseProfile] = useState<PlatformProfile | null>(null)
  const q = useDebouncedValue(searchDraft.trim(), 300)
  const action = usePlatformAction()

  useEffect(() => setOffset(0), [q, includeSimulated])

  const { data, isLoading, error } = usePlatformProfiles({
    q,
    includeSimulated,
    offset,
    limit: PLATFORM_PAGE_SIZE,
  })

  if (error instanceof PlatformAreaError && (error.status === 401 || error.status === 403)) {
    return <PlatformOnlyNotice title="Profiles" />
  }

  const confirmMark = () => {
    if (!markProfile) return
    action.mutate(
      { kind: 'mark-profile', profileId: markProfile.id, note },
      {
        onSuccess: (run) => toast(run.summary),
        onError: (err) => toast.error(err.message),
      },
    )
    setMarkProfile(null)
    setNote('')
  }

  const confirmReparse = () => {
    if (!reparseProfile) return
    action.mutate(
      { kind: 'reparse-profile', profileId: reparseProfile.id },
      {
        onSuccess: (run) =>
          toast(run.status === 'running' ? 'Re-parsing content. The row updates when it finishes.' : run.summary),
        onError: (err) => toast.error(err.message),
      },
    )
    setReparseProfile(null)
  }

  const total = data?.total ?? 0

  return (
    <PageContainer className="space-y-6">
      <PageHeaderStack>
        <PageHeader
          leading={<PageHeaderLeading />}
          title="Profiles"
          description="Every candidate profile, with skills, evidenced skills, experience and CV. Ask a candidate to improve, or re-parse what they contributed."
          icon={<UserRound className="h-5 w-5" />}
          actions={
            <PageHeaderActions
              secondary={
                <PageHeaderActionSecondary asChild>
                  <Link to="/platform/actions?target_type=profile">
                    <History className="h-4 w-4" aria-hidden />
                    Action log
                  </Link>
                </PageHeaderActionSecondary>
              }
            />
          }
        />
        <div className="flex flex-col gap-3">
          <ListBrowseToolbar
            searchId="platform-profiles-search"
            searchPlaceholder="Search name, email or headline…"
            searchValue={searchDraft}
            onSearchChange={setSearchDraft}
          />
          <div className="flex flex-wrap items-center gap-4">
            <IncludeSimulatedSwitch
              id="platform-profiles-simulated"
              checked={includeSimulated}
              onCheckedChange={setIncludeSimulated}
            />
            <p className="text-sm text-muted-foreground" role="status">
              {isLoading ? 'Loading…' : `${total} profile${total === 1 ? '' : 's'}`}
            </p>
          </div>
        </div>
      </PageHeaderStack>

      {error && !(error instanceof PlatformAreaError && error.status < 500) ? (
        <p className="text-sm text-destructive">{(error as Error).message}</p>
      ) : null}

      {isLoading ? <Skeleton className="h-64 w-full" /> : null}

      {!isLoading && !error && total === 0 ? (
        <p className="text-sm text-muted-foreground">No profiles match these filters.</p>
      ) : null}

      {data && data.results.length > 0 ? (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Candidate</TableHead>
                <TableHead>Completeness</TableHead>
                <TableHead className="text-right">Skills</TableHead>
                <TableHead className="text-right">Evidenced</TableHead>
                <TableHead className="text-right">Experience</TableHead>
                <TableHead>CV</TableHead>
                <TableHead>Last Platform action</TableHead>
                <TableHead>
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.results.map((profile) => (
                <TableRow key={profile.id}>
                  <TableCell className="align-top">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{profile.name}</span>
                      {profile.is_simulation ? <SimulatedBadge /> : null}
                    </div>
                    <div className="text-xs text-muted-foreground">{profile.email}</div>
                    {profile.headline ? (
                      <div className="max-w-[16rem] truncate text-xs text-muted-foreground">{profile.headline}</div>
                    ) : null}
                  </TableCell>
                  <TableCell className="align-top">
                    <CompletenessCell profile={profile} />
                  </TableCell>
                  <TableCell className="align-top text-right tabular-nums">{profile.skills}</TableCell>
                  <TableCell className="align-top text-right tabular-nums">{profile.evidenced_skills}</TableCell>
                  <TableCell className="align-top text-right tabular-nums">{profile.experiences}</TableCell>
                  <TableCell className="align-top">
                    <CvCell cv={profile.cv} />
                  </TableCell>
                  <TableCell className="align-top">
                    <LastActionCell run={profile.last_action} targetType="profile" targetId={profile.id} />
                  </TableCell>
                  <TableCell className="align-top text-right">
                    <ProfileActions
                      profile={profile}
                      onMark={() => setMarkProfile(profile)}
                      onReparse={() => setReparseProfile(profile)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : null}

      <ListPager total={total} offset={offset} disabled={isLoading} onOffsetChange={setOffset} />

      <Dialog
        open={markProfile !== null}
        onOpenChange={(open) => {
          if (!open) {
            setMarkProfile(null)
            setNote('')
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark for improvement</DialogTitle>
            <DialogDescription>
              {markProfile?.is_simulation
                ? 'This is a simulated profile, so no one is notified. The mark is still logged.'
                : `${markProfile?.name} gets a notification and email asking them to improve their profile, with what is missing.`}
            </DialogDescription>
          </DialogHeader>
          <FormField htmlFor="platform-mark-note" label="Note to the candidate (optional)">
            <Textarea
              id="platform-mark-note"
              value={note}
              maxLength={1000}
              placeholder="For example: add dates and a short description to each role."
              onChange={(event) => setNote(event.target.value)}
            />
          </FormField>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setMarkProfile(null)}>
              Cancel
            </Button>
            <Button type="button" onClick={confirmMark}>
              {markProfile?.is_simulation ? 'Mark' : 'Mark and notify'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={reparseProfile !== null} onOpenChange={(open) => !open && setReparseProfile(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Re-parse content?</AlertDialogTitle>
            <AlertDialogDescription>
              {reparseProfile?.name}’s CV and profile text are read again and merged into their skills and the
              skills evidenced on their roles. Nothing they wrote is deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmReparse}>Re-parse content</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  )
}
